/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useState } from 'react'

import { useRouter } from 'next/router'

import { getCookies } from 'cookies-next'

import { type GetServerSideProps, type GetServerSidePropsContext } from 'next'

import { useTranslations } from 'next-intl'

import { v4 as uuidv4 } from 'uuid'

import BlogList from '@/components/BlogList'
import CategorySort from '@/components/Category/CategorySort'
import HomeBanner from '@/components/HomeBanner'
import ImageOptimize from '@/components/ImageOptimize'
import { Loading } from '@/components/Loading/Loading'
import PageBreadcrumb from '@/components/PageBreadcrumb'
import ProductCard from '@/components/ProductCard'
import ProductCardSkeleton from '@/components/Skeleton/ProductCardSkeleton'
import { BannerType } from '@/constants'
import { useGetProductsByCategory } from '@/hooks/useProductHook'
import { Meta } from '@/layouts/Meta'
import { Main } from '@/templates/Main'

import type { BannerList, Product, ProductResponse, QueryParam } from '@/types'
import { setContext } from '@/utils/api-interceptors'
import {
  getBanner,
  getBottomMenu,
  getConfig,
  getMenus,
  getProductWithPaginate,
  getProvinces,
  getQuickMenu,
  getSaleRegion
} from '@/utils/endpoint'
import { useSearchParams } from '@/utils/next-util'

export const sortByTypes = [
  { label: 'Nên mua', type: 'custom', name: 'shouldBuy' },
  { label: 'Bán chạy', type: 'custom', name: 'bestSelling' },

  //   { label: 'Trả góp', type: 'custom', name: 'allowInstallment' },
  { label: 'Giá tăng dần', type: 'sortType', name: 'asc' },
  { label: 'Giá giảm dần', type: 'sortType', name: 'desc' }
]

const SearchPage = ({ productSearchData, banner }: { productSearchData: ProductResponse; banner: BannerList[] }) => {
  const t = useTranslations('Common')

  const router = useRouter()

  const [totalProduct, setTotalProduct] = useState<number>(productSearchData?.total || 0)

  const [products, setProducts] = useState<Product[]>(productSearchData?.data)
  const { getProducts } = useGetProductsByCategory()
  const [totalPages, setTotalPages] = useState(productSearchData?.pages)
  const [routerChange, setRouterChange] = useState(false)
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  const [currentParams, setCurrentParams] = useSearchParams([
    {
      name: 'page',
      value: null
    },
    {
      name: 'query',
      value: null
    },
    {
      name: 'sortBy',
      value: null
    },
    {
      name: 'sortType',
      value: null
    },
    {
      name: 'bestSelling',
      value: null
    },
    {
      name: 'shouldBuy',
      value: null
    },
    {
      name: 'allowInstallment',
      value: null
    }
  ])

  useEffect(() => {
    if (productSearchData?.data) {
      setProducts(productSearchData?.data)
      setTotalProduct(productSearchData?.total)
    }

    return () => {
      setCurrentPage(1)
    }
  }, [productSearchData])

  const [sortByOptions] = useState(sortByTypes)
  const activeParams = currentParams

  const activeSort = activeParams.find((filter: any) => filter.name === 'sortType').value

  const activeSortCustom = activeParams
    .filter((filter: any) => filter.value === 'true')
    .map((item: { name: string }) => item.name) || ['']

  const updateParams = useCallback(
    (params: any, routerChange = true) => {
      setLoading(true)
      setCurrentPage(1)
      setProducts([])

      if (routerChange) {
        setRouterChange(true)
      }

      const newFilters = activeParams.map((filter: any) => {
        const matchedParam = params?.find((p: any) => p.name === filter.name)

        return matchedParam ? { ...filter, value: matchedParam?.value } : filter
      })

      setCurrentParams(newFilters)
    },
    [activeParams]
  )

  useEffect(() => {
    if (!routerChange) {
      return
    }

    setLoading(true)
    setProducts([])

    if (router) {
      getProducts(
        {
          limit: 30,
          currentPage: currentPage,
          sortBy: (router?.query.sortBy as string) || 'score',
          sortType: (router?.query.sortType as string) || 'desc',
          search: router?.query?.query
        },
        {
          onSuccess: res => {
            setProducts(res.data.data)
            setLoading(false)
            setTotalPages(res.data.pages)
            setTotalProduct(res.data.total)
          }
        }
      )
    }
  }, [router.query])

  const fetchMoreProducts = () => {
    setCurrentPage(currentPage + 1)
    setLoading(true)
    getProducts(
      {
        limit: 30,
        currentPage: currentPage + 1,
        sortBy: (router?.query.sortBy as string) || 'score',
        sortType: (router?.query.sortType as string) || 'desc',
        search: router?.query?.query
      },
      {
        onSuccess: res => {
          setProducts(products.concat(res.data.data))
          setLoading(false)
        },
        onError: () => {
          setLoading(false)
        }
      }
    )
  }

  const breadcrumbs = [{ label: 'Kết quả tìm kiếm', href: '/' }]

  const middleBanner = useMemo(() => {
    return banner.filter(banner => {
      return banner.type === BannerType.HOME && banner.status === 1
    })
  }, [banner])

  return (
    <Main meta={<Meta title={'Search'} description={'description'} />}>
      <PageBreadcrumb homeText='Trang chủ' className='' breadcrumbs={breadcrumbs} />
      <section className='ProductSearchResult relative mb-[120px] rounded-xl bg-white p-3 pb-12'>
        <div className='grid grid-cols-2 items-center justify-between gap-3'>
          <h3 className='mb-3 mt-1 text-base font-bold text-dark-400'>
            {totalProduct} kết quả tìm kiếm cho từ khóa “{router?.query?.query}”
          </h3>
          <div className='flex w-full justify-end text-sm'>
            <CategorySort
              sortOptions={sortByOptions}
              onChange={updateParams}
              activeSort={activeSort}
              activeSortCustom={activeSortCustom}
            />
          </div>
        </div>

        {loading && (
          <div className='products mt-4 grid grid-cols-5 gap-2'>
            {[...new Array(20)].map(() => (
              <ProductCardSkeleton key={uuidv4()} />
            ))}
          </div>
        )}
        {products && products.length > 0 ? (
          <>
            <div className='products mt-3 grid grid-cols-5 gap-2'>
              {products.map((product, idx) => (
                <ProductCard layout={5} product={product} border={true} key={product._id + idx + 'list'} type='list' />
              ))}
            </div>
            {currentPage < totalPages && (
              <div
                className='loadMore absolute -bottom-3 left-1/2 mx-auto flex w-max min-w-[200px] -translate-x-1/2 cursor-pointer items-center justify-center gap-2 rounded-2xl border border-primary-base bg-white px-3 py-1.5 font-medium'
                onClick={fetchMoreProducts}
              >
                {loading ? (
                  <Loading />
                ) : (
                  <>
                    <span className='text-center text-base text-primary-base'>
                      Xem thêm {totalProduct - products.length} sản phẩm
                    </span>
                  </>
                )}
              </div>
            )}
          </>
        ) : (
          ''
        )}

        {products && products.length == 0 && !loading && (
          <div className='mt-[44px] flex flex-col items-center justify-center'>
            <ImageOptimize src='/no-product.svg' alt={t('notFoundProductFit')} width={500} height={372} />
            <span className='mt-[20px] text-HeadlineMedium font-normal'>{t('notFoundProductFit')}</span>
          </div>
        )}
      </section>
      <HomeBanner banner={middleBanner[1]?.items} layout={middleBanner[1]?.layout} />
      <BlogList />
    </Main>
  )
}

export default SearchPage

export const getServerSideProps: GetServerSideProps = async (context: GetServerSidePropsContext) => {
  const { req, res, query } = context

  const params = {
    ...query,
    page: 1,
    fullTextSearch: query?.query,
    sortBy: query?.sortBy ? query?.sortBy : 'score',
    sortType: query?.sortType ? query?.sortType : 'desc',
    searching: true,
    limit: 30
  }

  const { token, language, messages } = await setContext(getCookies({ req, res }), {
    language: context.locale
  })

  const [stores, menus, banner, quickMenu, productSearchData, bottomMenu, regions, systemConfig] = await Promise.all([
    getProvinces(),
    getMenus(),
    getBanner(),
    getQuickMenu(),
    getProductWithPaginate(params as QueryParam),
    getBottomMenu(),
    getSaleRegion(),
    getConfig()
  ])

  return {
    props: {
      stores,
      menus,
      banner,
      language,
      messages,
      quickMenu,
      bottomMenu,
      productSearchData,
      regions,
      systemConfig,
      token: token || null
    }
  }
}
